<template>
    <div>

        <toolbox>
            <toolbox-button :help-text="$t('searchQuery.exportExcel')" @press="handleExport">
                <icon :icon="['fal', 'file-excel']" size="lg"/>
            </toolbox-button>
        </toolbox>

        <framed-content :title="$t('searchQuery.queries')" v-if="queryStoreName">
            <form-wrapper :form-title="$t('searchQuery.savedQuery')">
                <form-select-input :caption="$t('searchQuery.loadQuery')" v-model="loadedQuery"
                                   :options="queryOptions"/>
                <form-text-input :caption="$t('searchQuery.queryName')" v-model="loadedQueryName"/>
                <form-text-input :caption="$t('searchQuery.queryPurpose')" v-model="loadedQueryPurpose"/>
                <form-checkbox-input :caption="$t('searchQuery.locked')" v-model="loadedQueryLocked"/>
            </form-wrapper>

            <filter-form
              :filter-definition="queryDefinition"
              v-model="searchQuery"
              :key="formResetDate"
            />
            <simple-horizontal-layout>
                <empty/>
                <empty/>
                <empty/>
                <simple-button @press="clearQuery">{{ $t('searchQuery.clearQuery') }}</simple-button>
                <simple-button @press="saveQuery">{{ $t('searchQuery.saveQuery') }}</simple-button>
                <simple-button @press="refreshData">{{ $t('searchQuery.doQuery') }}</simple-button>
            </simple-horizontal-layout>
        </framed-content>

        <collapsible-content>
            <template slot="header">
                {{ $t('searchQuery.content') }}
            </template>
            <template v-if="loading">
                <loader indefinite :message="$t('searchQuery.loading', {objectName})"/>
            </template>
            <template>
                <data-table :data="actualData" :pagination="listPagination"
                            @pageChanged="handlePageChanged"
                            @sortChanged="handleSortChanged"
                            @selectionChanged="handleSelectionChanged"
                            @focusedChanged="handleFocusedChanged"
                            @delete="handleDelete"
                            :configuration="tableConfig"
                            v-on="$listeners"
                            :component-id="`simple-list-${objectName}`"
                >
                    <slot/>
                </data-table>
            </template>
        </collapsible-content>
    </div>
</template>

<script>
import DataTable from "../table/DataTable";
import FormWrapper from "../form/FormWrapper";
import CollapsibleContent from "../CollapsibleContent";
import FormSelectInput from "../form/FormSelectInput";
import SimpleButton from "../button/SimpleButton";
import SimpleHorizontalLayout from "../layout/SimpleHorizontalLayout";
import Empty from "../layout/Empty";
import FormTextInput from "../form/FormTextInput";
import FilterForm from "../filters/FilterForm";
import QueryUtils from "../../utils/QueryUtils";
import { mapGetters } from "vuex";
import Loader from "../Loader";
import FormCheckboxInput from "../form/FormCheckboxInput";
import FramedContent from "../layout/FramedContent";
import ToolboxButton from "@/components/toolbox/ToolboxButton";
import Toolbox from "@/components/toolbox/Toolbox";
import handleTableExport from '@/utils/excel-utils'

export default {
    name: "SimpleList",
    components: {
        Toolbox,
        ToolboxButton,
        FramedContent,
        FormCheckboxInput,
        Loader,
        FilterForm,
        FormTextInput,
        Empty,
        SimpleHorizontalLayout, SimpleButton, FormSelectInput, CollapsibleContent, FormWrapper, DataTable
    },
    props: {
        objectName: String,
        columns: Array,
        actions: Array,
        listData: Array,
        queryStoreName: String,
        config: Object,
        defaultSort: Object
    },
    data: function () {
        return {
            loadedQuery: null,
            loadedQueryName: null,
            loadedQueryPurpose: "",
            loadedQueryLocked: false,
            searchQuery: {parameters: {}},
            formResetDate: new Date().getTime(),
            sorting: null,
            loading: true
        }
    },
    computed: {
        ...mapGetters({
            currencies: "currencies/currencies",
            currencyOptions: "currencies/currenciesOptions",
            accounts: "banking/accounts",
            parties: "parties/parties"
        }),
        capitalQueryStoreName() {
            if (!this.queryStoreName) return "";
            return this.queryStoreName.charAt(0).toUpperCase() + this.queryStoreName.substring(1);
        },
        tableConfig() {
            let {
                tableConfiguration: {
                    enableCheck = true,
                    allowEdit = true,
                    enableAdd = false
                }
            } = this.config || {tableConfiguration: {}};
            let result = {
                enableCheck: enableCheck,
                allowEdit: allowEdit,
                enableAdd: enableAdd
            };
            result.columns = this.columns;
            result.actions = [
                ...(this.actions ?? []),
                {
                    name: 'delete',
                    icon: 'trash',
                    tooltip: "Delete item"
                }];
            return result;
        },
        queryDefinition() {
            if (!this.queryStoreName) return {};
            return QueryUtils.getByType(this.objectName, {
                currencies: this.currencyOptions, accounts: this.accounts,
                parties: this.parties.map(party => ({
                    id: party.id, name: party.displayName
                }))
            });
        },
        queryOptions() {
            if (!this.queryStoreName) return [];
            return this.$store.getters[`${this.queryStoreName}Queries/queriesByType`](this.objectName);
        },
        actualData() {
            return this.$store.getters[this.objectName + 's/' + this.objectName + "s"]
        },
        listPagination() {
            return this.$store.getters[this.objectName + 's/' + this.objectName + "Pagination"]
        }
    },
    methods: {
        async handleExport() {
            let resultList = await this.$store.dispatch(`${this.objectName}s/fetch${this.objectName.charAt(0).toUpperCase() +
            this.objectName.substring(1)}s`, {
                page: 0,
                size: 5000,
                query: this.searchQuery,
                sort: this.sorting
            });
            handleTableExport(this.columns, resultList, this.objectName);

        },
        clearQuery() {
            this.searchQuery = {parameters: {}};
            this.formResetDate = new Date().getTime();
            this.loadedQueryName = "";
            this.handlePageChanged();
        },
        saveQuery() {
            let toSave = this.$_.cloneDeep(this.searchQuery);
            if (this.loadedQueryName == "") {
                return;
            }
            toSave.name = this.loadedQueryName;
            toSave.object = this.objectName;
            this.$store.dispatch(`${this.queryStoreName}Queries/save${this.capitalQueryStoreName}Query`, toSave)
              .then(() => {
                  this.$notify({
                      group: 'application',
                      title: `Query ${toSave.name} saved`,
                      text: `Query ${this.cashEventQueryName} was successfully saved`,
                      type: "ok"
                  });
              })
              .then(() => {
                  this.refreshQueries();
              });
        },
        refreshQueries() {
            return this.$store.dispatch(`${this.queryStoreName}Queries/refresh${this.capitalQueryStoreName}Queries`)

        },
        refreshData(requestedPage) {
            console.log('XX', requestedPage)
            this.loading = true;
            return this.$store.dispatch(`${this.objectName}s/refresh${this.objectName.charAt(0).toUpperCase() +
            this.objectName.substring(1)}sPaged`, {
                page: requestedPage,
                size: this.config?.pageSize,
                query: this.searchQuery,
                sort: this.sorting
            })
              .then(() => {
                  this.loading = false;
              })
        },
        handlePageChanged(page) {
            this.refreshData(page);
        },
        handleSortChanged({sortField, direction}) {
            this.$set(this, 'sorting', {sortField, direction})
            this.refreshData();
        },
        handleSelectionChanged(entries) {
            this.$emit('selection-changed', entries);
        },
        handleFocusedChanged(entry) {
            this.$emit('focused-changed', entry);
        },
        handleActions(entry) {

        },
        handleDelete(entry) {
            if (this.$listeners['delete']) {
                this.$listeners.delete(entry);
            } else {
                this.$store.dispatch(`${this.objectName}s/delete${this.objectName.charAt(0).toUpperCase() +
                this.objectName.substring(1)}`, entry)
            }
        }

    },
    watch: {
        loadedQuery: {
            handler(newVal) {
                let loadedQuery = this.$store.getters[`${this.queryStoreName}Queries/queryById`](newVal);
                this.searchQuery = loadedQuery;
                this.loadedQueryName = loadedQuery.name;
                this.handlePageChanged();
            }
        },
    },
    mounted() {
        this.$nextTick(() => {
            if (this.defaultSort)
                this.$set(this, 'sorting', this.defaultSort)
            this.refreshQueries();
            this.refreshData(0);
        });
    }
}
</script>

<style scoped>

</style>